import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AuthError, AuthShell, PRIMARY_BUTTON } from '@/features/auth/auth-shell'
import {
  useLoginWithPasskey,
  useLoginWithPassword,
  useLoginWithTotp,
  useRequestPasswordReset,
  useSession,
} from '@/features/auth/use-auth'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'

/**
 * Sign-in.
 *
 * E-mail, password, then the code from the authenticator app. Two steps, both
 * required — the password on its own never produces a session.
 *
 * A passkey is offered underneath for anyone who has set one up. It is one step
 * rather than two and cannot be phished, but it is nobody's only way in: an
 * authenticator that will not cooperate must never be the reason somebody
 * cannot get to a handover appointment.
 *
 * "Passwort vergessen?" replaces the password and nothing else — the
 * authenticator code is still asked afterwards, so a mailbox alone never opens
 * the account. Without a mail server the form is not shown at all: it would
 * promise a message that only ever reaches the server log.
 */
type Mode = 'password' | 'mfa' | 'passkey' | 'reset'

export function LoginPage() {
  const { user, isLoading, mailConfigured } = useSession()
  const location = useLocation()

  const password = useLoginWithPassword()
  const totp = useLoginWithTotp()
  const passkey = useLoginWithPasskey()
  const reset = useRequestPasswordReset()

  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [resetEmail, setResetEmail] = useState('')

  if (isLoading) return null

  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? '/checklists'} replace />
  }

  const restart = () => {
    setMode('password')
    setCode('')
    setSecret('')
    totp.reset()
    password.reset()
    passkey.reset()
    reset.reset()
  }

  return (
    <AuthShell
      title="Anmelden"
      description="Interne Anwendung der aCasaImmobilien GmbH. Zugang nur für eingerichtete Konten."
      footer="Kein Zugang? Ein Administrator legt das Konto an und schickt einen einmaligen Einladungslink."
    >
      {mode === 'mfa' ? (
        /* ------------------------------------------------ second factor --- */
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            totp.mutate(code)
          }}
        >
          <p className="text-muted text-xs leading-relaxed">
            Noch der 6-stellige Code aus der Authenticator-App.
          </p>

          <TextInput
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-stelliger Code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            // The only field on screen, and the step follows directly from the
            // one before it.
            autoFocus
            required
          />

          <button
            type="submit"
            className={PRIMARY_BUTTON}
            disabled={totp.isPending || code.length !== 6}
          >
            {totp.isPending ? 'wird geprüft …' : 'Anmelden'}
          </button>

          <AuthError message={errorMessage(totp.error)} />
          <BackLink onClick={restart} />
        </form>
      ) : mode === 'reset' ? (
        /* ---------------------------------------------- forgotten password --- */
        !mailConfigured ? (
          <div className="flex flex-col gap-3">
            <p className="text-muted text-sm leading-relaxed">
              Dieser Server kann noch keine E-Mails versenden. Ein Administrator kann unter
              Einstellungen › Konten einen Link zum Setzen eines neuen Passworts ausstellen.
            </p>
            <BackLink onClick={restart} />
          </div>
        ) : reset.isSuccess ? (
          // One sentence whatever the address: saying "no such account" here
          // would turn this form into a directory of who works at the agency.
          <div className="flex flex-col gap-3">
            <p className="text-muted text-sm leading-relaxed">
              Falls zu dieser Adresse ein Konto gehört, ist eine E-Mail mit einem Link unterwegs.
              Der Link ist nur kurze Zeit gültig.
            </p>
            <BackLink onClick={restart} />
          </div>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              reset.mutate(resetEmail.trim())
            }}
          >
            <p className="text-muted text-xs leading-relaxed">
              Wir schicken einen Link, mit dem sich ein neues Passwort festlegen lässt. Der Code aus
              der Authenticator-App wird danach weiterhin abgefragt.
            </p>

            <TextInput
              type="email"
              autoComplete="username"
              placeholder="E-Mail"
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              autoFocus
              required
            />

            <button
              type="submit"
              className={PRIMARY_BUTTON}
              disabled={reset.isPending || resetEmail.trim().length === 0}
            >
              {reset.isPending ? 'wird gesendet …' : 'Link anfordern'}
            </button>

            <AuthError message={errorMessage(reset.error)} />
            <BackLink onClick={restart} />
          </form>
        )
      ) : mode === 'passkey' ? (
        /* --------------------------------------------- passkey, if any --- */
        <div className="flex flex-col gap-3">
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={passkey.isPending}
            onClick={() => passkey.mutate(undefined)}
          >
            {passkey.isPending ? 'warte auf Bestätigung …' : 'Mit Passkey anmelden'}
          </button>

          <p className="text-faint text-xs leading-relaxed">
            Nur, wenn auf diesem Gerät ein Passkey eingerichtet wurde. Face ID, Fingerabdruck oder
            die Geräte-PIN — kein zusätzlicher Code nötig.
          </p>

          <AuthError message={errorMessage(passkey.error)} />
          <BackLink onClick={restart} />
        </div>
      ) : (
        /* ------------------------------------------------- first factor --- */
        <>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              password.mutate(
                { email: email.trim(), password: secret },
                { onSuccess: () => setMode('mfa') },
              )
            }}
          >
            <TextInput
              type="email"
              autoComplete="username"
              placeholder="E-Mail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <TextInput
              type="password"
              autoComplete="current-password"
              placeholder="Passwort"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              required
            />

            <button
              type="submit"
              className={PRIMARY_BUTTON}
              disabled={password.isPending || email.trim().length === 0 || secret.length === 0}
            >
              {password.isPending ? 'wird geprüft …' : 'Weiter'}
            </button>

            <p className="text-faint text-xs leading-relaxed">
              Danach folgt der Code aus der Authenticator-App.
            </p>

            <AuthError message={errorMessage(password.error)} />
          </form>

          <div className="border-line mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t pt-5">
            <button
              type="button"
              onClick={() => {
                password.reset()
                setMode('passkey')
              }}
              className="text-muted hover:text-ink text-xs underline underline-offset-4 transition-colors"
            >
              Stattdessen Passkey verwenden
            </button>
            <button
              type="button"
              onClick={() => {
                password.reset()
                // Whatever was typed above is almost always the address meant.
                setResetEmail(email.trim())
                setMode('reset')
              }}
              className="text-muted hover:text-ink text-xs underline underline-offset-4 transition-colors"
            >
              Passwort vergessen?
            </button>
          </div>
        </>
      )}
    </AuthShell>
  )
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-faint hover:text-ink self-start text-[11px] underline underline-offset-4 transition-colors"
    >
      zurück
    </button>
  )
}

/**
 * A cancelled passkey prompt is not a failure worth a red box — it is somebody
 * changing their mind — so it is reported as its own quiet sentence.
 */
function errorMessage(error: unknown): string | null {
  if (!error) return null

  if (error instanceof ApiRequestError) return error.message

  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'Abgebrochen oder nichts ausgewählt.'
    if (error.name === 'InvalidStateError') return 'Dieses Gerät ist bereits registriert.'
    return error.message
  }

  return 'Unbekannter Fehler'
}
